import React, {useMemo, useState} from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {isSpecialBin, MAP_KEY_SEP_CHAR, parseBinName} from '../../utils'
import BinSelector from './BinSelector'
import {useNavigate} from 'react-router-dom'

interface BinSummaryProps {
    binMap: Record<string, BinTotals>
    totals: BinSummaryTotals
    currentBin: string | null
    onSetCurrentBin: (currentBin: string) => void
    row: string
    allBins: string[]
    processSubmitReport: () => void
}

interface BinTotals {
    serials: number
    nonSerials: number
    excludes: number
}

type BinSummaryTotals = {
    totalBins: number
    totalSerials: number
    totalNonSerials: number
    totalExcluded: number
    totalSaved: number
    row: string
}

type GroupedData = {
    [row: string]: {
        [bin: string]: BinTotals
    }
}

type RowSummary = {
    binsCounted: number
    serialCount: number
    nonSerialCount: number
    excludedCount: number
    totalCount: number
    row: string
}

type RowSummaries = {
    [row: string]: RowSummary
}

function BinSummary({
                        binMap,
                        totals,
                        onSetCurrentBin,
                        row,
                        allBins,
                        processSubmitReport
                    }: BinSummaryProps) {
    const [filterText, setFilterText] = useState('')
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
    const [openDialog, setOpenDialog] = useState(false)
    const navigate = useNavigate()

    const {groupedData, rowSummaries} = useMemo(() => {
        const grouped: GroupedData = {}
        const summaries: RowSummaries = {}

        const sortKeys = (keys: string[]): string[] => {
            return keys.sort((a, b) => {
                let aIsSpecial = isSpecialBin(a)
                let bIsSpecial = isSpecialBin(b)
                if (aIsSpecial && bIsSpecial) return a.localeCompare(b)
                if (aIsSpecial) return -1
                if (bIsSpecial) return 1
                return a.localeCompare(b)
            })
        }

        for (let key in binMap) {
            const totals = binMap[key]
            let bin = key.split(MAP_KEY_SEP_CHAR)[0]
            let binParts = bin.split(' ')
            let row = binParts[0]

            if (binParts.length == 3) row = binParts[1]

            if (!summaries[row])
                summaries[row] = {
                    binsCounted: 0,
                    serialCount: 0,
                    nonSerialCount: 0,
                    excludedCount: 0,
                    totalCount: 0,
                    row: ''
                }

            if (!grouped[row]) grouped[row] = {}

            if (!grouped[row][bin]) {
                summaries[row].binsCounted++
                grouped[row][bin] = {serials: 0, nonSerials: 0, excludes: 0}
            }

            summaries[row].serialCount += totals.serials
            summaries[row].nonSerialCount += totals.nonSerials
            summaries[row].excludedCount += totals.excludes
            summaries[row].totalCount += (totals.serials + totals.nonSerials)
            summaries[row].row = row

            grouped[row][bin].serials += totals.serials
            grouped[row][bin].nonSerials += totals.nonSerials
            grouped[row][bin].excludes += totals.excludes
        }


        // sort the bins within each row in the grouped data
        const sortedGrouped: GroupedData = {}
        for (let row in grouped) {
            sortedGrouped[row] = {}
            const sortedBins = sortKeys(Object.keys(grouped[row]))
            for (let bin of sortedBins) {
                sortedGrouped[row][bin] = grouped[row][bin]
            }
        }

        // sort the row summaries by bin
        const sortedSummaries: RowSummaries = {}
        const sortedRows = sortKeys(Object.keys(summaries))
        for (let row of sortedRows) {
            sortedSummaries[row] = summaries[row]
        }

        return {groupedData: sortedGrouped, rowSummaries: sortedSummaries}
    }, [binMap])

    const handleToggleExpand = (row: string) => {
        setExpandedRows(prev => {
            let newExpandedRows = new Set(prev)
            if (newExpandedRows.has(row)) {
                newExpandedRows.delete(row)
            } else {
                newExpandedRows.add(row)
            }
            return newExpandedRows
        })
    }

    const binOptions = useMemo(() => {
        return Object.keys(binMap).map(
            binId => binId.split(MAP_KEY_SEP_CHAR)[0]
        )
    }, [binMap])

    const confirmSubmit = () => {
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
    }

    const handleConfirm = () => {
        processSubmitReport()
        handleClose()
        window.sessionStorage.setItem('submissionSuccess', 'true')
        navigate('/inventory/counts')
    }

    return (
        <Box p={3}>
            <Box textAlign="center" pb={2}>
                <h1 className="text-2xl font-medium">Bin Summary</h1>
                <h2 className="text-lg text-gray-600">
                    <span className="font-bold text-green-700">
                        Saved {totals.totalSaved} quantity:{' '}
                    </span>
                    {totals.totalBins === 1 ? `${totals.totalBins} bin` : `${totals.totalBins} bins`}, {totals.totalSerials === 1 ? `${totals.totalSerials} serial` : `${totals.totalSerials} serials`},{' '}
                    {totals.totalNonSerials === 1 ? `${totals.totalNonSerials} non-serial` : `${totals.totalNonSerials} non-serials`},{' '}
                    <span style={{color: 'red'}}>{totals.totalExcluded}{' '} excluded </span>
                </h2>
            </Box>

            <Box display="flex" justifyContent="space-between" pb={2}>
                <div className="flex flex-col">
                    <h3 className="text-md font-bold">Count new bin:</h3>
                    <BinSelector
                        binRow={row}
                        allBins={allBins}
                        onBinSelect={onSetCurrentBin}
                    />
                </div>
                <div className="flex flex-col">
                    <h3 className="text-md font-bold my-1">
                        Filter/edit counted bins:
                    </h3>
                    <Autocomplete
                        freeSolo
                        options={binOptions}
                        value={filterText}
                        onInputChange={(_e, newValue) =>
                            setFilterText(newValue)
                        }
                        onChange={(_e, newValue) => {
                            if (newValue) {
                                onSetCurrentBin(newValue)
                            }
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Filter/edit bins"
                                variant="outlined"
                                size="small"
                                sx={{width: 200}}
                            />
                        )}
                    />
                </div>
            </Box>

            <TableContainer component={Paper} sx={{maxHeight: 400, my: 2}}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{display: 'none'}}></TableCell>
                            <TableCell>Row</TableCell>
                            <TableCell>Bin</TableCell>
                            <TableCell>Serials</TableCell>
                            <TableCell>Non-Serials</TableCell>
                            <TableCell>Excluded</TableCell>
                            <TableCell>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.keys(rowSummaries).map(row => (
                            <React.Fragment key={row + '-header'}>
                                <TableRow sx={{backgroundColor: '#f0f0f0'}}>
                                    <TableCell className="font-bold">
                                        <Box display="flex" alignItems="center">
                                            <IconButton
                                                onClick={() =>
                                                    handleToggleExpand(row)
                                                }
                                                size="small">
                                                {expandedRows.has(row) ? (
                                                    <ExpandLessIcon />
                                                ) : (
                                                    <ExpandMoreIcon />
                                                )}
                                            </IconButton>
                                            {rowSummaries[row]?.row || ''}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {rowSummaries[row]?.binsCounted || 0}
                                    </TableCell>
                                    <TableCell>
                                        {rowSummaries[row]?.serialCount || 0}
                                    </TableCell>
                                    <TableCell>
                                        {rowSummaries[row]?.nonSerialCount || 0}
                                    </TableCell>
                                    <TableCell>
                                        {rowSummaries[row]?.excludedCount || 0}
                                    </TableCell>
                                    <TableCell>
                                        {rowSummaries[row]?.totalCount || 0}
                                    </TableCell>
                                </TableRow>
                                {expandedRows.has(row) &&
                                    Object.keys(groupedData[row] || {}).map(
                                        bin => {
                                            const binTotals = groupedData[row][
                                                bin
                                                ] || {
                                                serials: 0,
                                                nonSerials: 0,
                                                excludes: 0
                                            }

                                            return (
                                                <TableRow
                                                    key={row + '-' + bin}
                                                    hover
                                                    onClick={() =>
                                                        onSetCurrentBin(bin)
                                                    }
                                                    sx={{
                                                        cursor: 'pointer'
                                                    }}>
                                                    <TableCell
                                                        sx={{
                                                            display: 'none'
                                                        }}></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>{parseBinName(bin)}</TableCell>
                                                    <TableCell>
                                                        {binTotals.serials}
                                                    </TableCell>
                                                    <TableCell>
                                                        {binTotals.nonSerials}
                                                    </TableCell>
                                                    <TableCell>
                                                        {binTotals.excludes}
                                                    </TableCell>
                                                    <TableCell>
                                                        {binTotals.serials +
                                                            binTotals.nonSerials}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        }
                                    )}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={openDialog}
                onClose={handleClose}>
                <DialogTitle>
                    Confirm Submission
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        You are about to submit your report for
                        review. Please confirm if you wish to
                        continue.
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                    <Button onClick={handleClose} color="error">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Box display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => confirmSubmit()}>
                    Submit Report
                </Button>
            </Box>
        </Box>
    )
}

export default BinSummary
